import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';
import { NodeItemType } from '../enums/node-item-type';
import { Template } from '../interfaces/template';
import { FlatNodeItem } from '../models/flat-node-item';
import { NodeItem } from '../models/node-item';
import { CreateTemplateRequest } from './../models/create-template-request';
import { MaterialModule } from '../material';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';


@Component({
    selector: 'app-template-creator-modal',
    templateUrl: './template-creator-modal.component.html',
    imports: [MaterialModule,ReactiveFormsModule,ScrollingModule,CommonModule],
})
export class TemplateCreatorModalComponent implements OnInit, AfterViewInit {
  public vantaShotTemplate: any;

  public createdTemplate: Template;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  public flatNodeMap = new Map<FlatNodeItem, NodeItem>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  public nestedNodeMap = new Map<NodeItem, FlatNodeItem>();

  public treeControl: FlatTreeControl<FlatNodeItem>;

  public treeFlattener: MatTreeFlattener<NodeItem, FlatNodeItem>;

  public dataSource: MatTreeFlatDataSource<NodeItem, FlatNodeItem>;

  public selectionModel = new SelectionModel<FlatNodeItem>(true /* multiple */);

  public dataChange = new BehaviorSubject<NodeItem[]>([]);

  public get data(): NodeItem[] { return this.dataChange.value; }

  public createTemplateRequest: CreateTemplateRequest;

  private createdJsonConfig: any;

  public templateName: UntypedFormControl;

  constructor(public dialogRef: MatDialogRef<TemplateCreatorModalComponent>,
              @Inject(MAT_DIALOG_DATA) public input: any) {
      this.vantaShotTemplate = input.vantaShotTemplate;
      this.createdTemplate = input.jsonTemplate;
    }

  public ngOnInit() {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<FlatNodeItem>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.initCreateTemplateRequest();

    this.initialize();
    this.dataChange.subscribe(data => {
      this.dataSource.data = data;
    });
  }

  public ngAfterViewInit() {
    this.setSelection();
    this.treeControl.expandAll();
  }

  public getLevel = (node: FlatNodeItem) => node.level;

  public isExpandable = (node: FlatNodeItem) => node.expandable;

  public getChildren = (node: NodeItem): NodeItem[] => node.childrens;

  public hasChild = (_: number, nodeData: FlatNodeItem) => nodeData.expandable;

  public isObjectWithChild = (_: number, nodeData: FlatNodeItem) =>
    nodeData.expandable && nodeData.type === NodeItemType.ObjectNode

  public isArrayWithChild = (_: number, nodeData: FlatNodeItem) =>
    nodeData.expandable && nodeData.type === NodeItemType.ArrayNode

  public transformer = (node: NodeItem, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
        ? existingNode
        : new FlatNodeItem();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = !!node.childrens;
    flatNode.type = node.type;
    flatNode.selected = node.selected;
    flatNode.alias = node.alias;
    flatNode.max = flatNode.max !== undefined ? flatNode.max : 10;

    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  public initialize() {
    const data = this.buildTree(this.vantaShotTemplate, 0, null);
    this.dataChange.next(data);
  }

  public buildTree(obj: {[key: string]: any}, level: number, parent: NodeItem): NodeItem[] {
    return Object.keys(obj).reduce<NodeItem[]>((accumulator, key) => {
      const value = obj[key];
      const node = new NodeItem();
      node.item = key;
      node.parent = parent;

      if (value != null) {
        if (value instanceof Array) {
          node.type = NodeItemType.ArrayNode;
          node.childrens = this.buildTree(value[0], level + 1, node);
        } else if (value instanceof Object) {
          node.type = NodeItemType.ObjectNode;
          node.childrens = this.buildTree(value, level + 1, node);
        } else {
          node.type = NodeItemType.ObjectNode;
          node.item = key;
        }
      }

      node.path = this.getPath(node);
      this.setNodeData(node);

      return accumulator.concat(node);
    }, []);
  }

  public getPath(node: NodeItem): string {
    let path = node.item;
    let parent = node.parent;
    while (parent !== null) {
      const separator = parent.type === NodeItemType.ArrayNode ? '[].' : '.';
      path = parent.item + separator + path;
      parent = parent.parent;
    }
    return path;
  }

  public onCancelClick(): void {
    this.dialogRef.close();
  }

  public onSetAsDefaultClick(): void {
    // todo
  }

  public onSaveClick(): void {
    this.createTemplateRequest.jsonConfig = this.buildJsonConfig();
    this.createTemplateRequest.description = this.templateName.value;
    this.dialogRef.close(this.createTemplateRequest);
  }

  public toggleNode(flatNode: FlatNodeItem): void {
    const node = this.flatNodeMap.get(flatNode);
    node.selected = !node.selected;
    flatNode.selected = node.selected;

    if (node.selected && (node.type === NodeItemType.ObjectNode)) {
      const alias = this.camelCaseToSentenceCase(node.item);
      node.alias = alias;
      flatNode.alias = alias;
    } else {
      node.alias = null;
      flatNode.alias = null;
    }

    this.selectionModel.toggle(flatNode);
  }

  public setMaxItems($event, flatNode: FlatNodeItem): void {
    const node = this.flatNodeMap.get(flatNode);
    node.max = $event.value;
    flatNode.max = $event.value;
  }

  public isValid(): boolean {
    return this.createTemplateRequest.description !== undefined
      && this.createTemplateRequest.description !== null
      && this.createTemplateRequest.description !== '';
  }

  private camelCaseToSentenceCase(text: string): string {
    const result = text.replace( /([A-Z])/g, ' $1' );
    return result.charAt(0).toUpperCase() + result.slice(1); // capitalize the first letter
  }

  private buildJsonConfig(): string {
    const jsonConfig = {};
    const selectedNodes: Array<NodeItem> = this.selectionModel.selected.map((flatNode) => {
      const node = this.flatNodeMap.get(flatNode);
      node.alias = flatNode.alias;
      node.max = flatNode.max;
      return node;
    });

    selectedNodes.forEach((node) => {
      if (node.type === NodeItemType.ArrayNode) {
        jsonConfig[node.path] = {
          max: node.max
        };
      } else {
        jsonConfig[node.path] = {
          alias: node.alias
        };
      }
    });

    return JSON.stringify(jsonConfig);
  }

  private initCreateTemplateRequest(): void {
    if (this.createdTemplate) {
      this.createTemplateRequest = {
        id: this.createdTemplate.id,
        description: this.createdTemplate.description,
        jsonConfig: this.createdTemplate.jsonConfig,
        templateModel: 'VantaShot'
      };
      this.templateName = new UntypedFormControl(this.createdTemplate.description, Validators.required);
      this.createdJsonConfig = JSON.parse(this.createdTemplate.jsonConfig);
    } else {
      this.createTemplateRequest = {
        description: '',
        jsonConfig: null,
        templateModel: 'VantaShot'
      };
      this.templateName = new UntypedFormControl('', Validators.required);
    }
  }

  private setNodeData(node: NodeItem): void {
    if (this.createdTemplate) {
      node.selected = this.createdJsonConfig[node.path] !== null && this.createdJsonConfig[node.path] !== undefined;

      if (node.selected) {
        node.alias = this.createdJsonConfig[node.path].alias;
        node.max = this.createdJsonConfig[node.path].max;
      }
    }
  }

  private setSelection(): void {
    this.nestedNodeMap.forEach((node) => {
      if (node.selected) {
        this.selectionModel.select(node);
      }
    });
  }
}
